<template>
    <div class="activity-group p-2">
        <b-card
            v-for="activity in activitiesFilter"
            :id="`activity-${activity.id}`"
            @click="fill(activity)"
            :key="activity.id"
            class="text-center shadow"
            body-class="p-1"
        >
            <b-alert
                v-if="_.size(activity.dueCalculated)"
                :variant="activity.dueCalculated.color"
                show
                >{{ activity.dueCalculated.text }}</b-alert
            >
            <p class="px-1 my-1">
                {{ TREATMENT_TYPE[activity.treatment_activity_type_id] }}
            </p>
        </b-card>
        <b-card
            v-if="data.start === 0"
            @click="start"
            class="text-center shadow"
            body-class="p-1"
        >
            <b-alert show variant="info" class>Start</b-alert>
            <p class="px-1 my-1">Start</p>
        </b-card>
        <b-card
            v-if="data.start === 0"
            @click="removeTreatment"
            class="text-center shadow"
            body-class="p-1"
        >
            <b-alert show variant="delete" class>Remove</b-alert>
            <p class="px-1 my-1">Remove</p>
        </b-card>
        <b-card
            v-if="
                data.start > 0 && status.status_id > TREATMENT_STATUS.ADMITTED
            "
            class="text-center shadow"
            @click="end"
            body-class="p-1"
        >
            <b-alert show variant="info" class>End</b-alert>
            <p class="px-1 my-1">End</p>
        </b-card>
        <b-card
            v-for="activity in activitiesCompletedFilter"
            :id="`activity-${activity.id}`"
            :key="activity.id"
            class="text-center shadow"
            body-class="p-1"
            @click="summary(activity)"
        >
            <b-alert variant="dark" show v-if="activity.complete_at">{{
                _toDateTime(activity.complete_at)
                    .setZone(
                        _setting.timezone
                            ? _setting.timezone
                            : "America/Los_Angeles"
                    )
                    .toFormat("hh:mm a")
            }}</b-alert>
            <b-alert variant="dark" show v-else-if="activity.status === 3"
                >Deactivated</b-alert
            >
            <b-alert variant="dark" show v-else>Incomplete</b-alert>
            <p class="px-1 my-1">
                {{ TREATMENT_TYPE[activity.treatment_activity_type_id] }}
            </p>
        </b-card>
        <b-popover
            id="`activity-popover-${activity.id}`"
            :delay="{ show: 1000, hide: 500 }"
            v-for="activity in popOverFilter"
            :target="`activity-${activity.id}`"
            :key="`activity-popover-${activity.id}`"
            custom-class="activity-popover"
            triggers="hover focus"
            boundary="window"
        >
            <h5 class="pt-2 px-2">Activity Summary</h5>
            <hr class="mt-0 mb-1" />
            <summary-list
                ref="summaryList"
                class="px-2"
                :params="{
                    ...activity,
                    patient: data.patient,
                    staff: data.staff,
                    module: 'hover'
                }"
                @loading="loading = true"
                @loaded="loading = false"
            ></summary-list>
            <hr class="mb-0" />
            <b-button
                v-if="_hasAccess('schedule.treatmentboard.update')"
                @click="edit(activity)"
                variant="link"
            >
                <span v-if="activity.status === 3"
                    ><i class="famfamfam-silk add"></i>Reactivate</span
                >
                <span v-if="activity.status !== 3"
                    ><i class="famfamfam-silk page_white_edit"></i>Edit</span
                >
            </b-button>
            <b-button
                v-if="_hasAccess('schedule.treatmentboard.create')"
                @click="duplicate(activity)"
                variant="link"
            >
                <span v-if="activity.status !== 3"
                    ><i class="cus-page-copy"></i>Duplicate</span
                >
            </b-button>
            <b-button
                v-if="_hasAccess('schedule.treatmentboard.delete')"
                @click="deleteModal(activity)"
                variant="link"
            >
                <span v-if="activity.status === 1"
                    ><i class="famfamfam-silk delete"></i>Delete</span
                >
                <span v-if="activity.status === 0"
                    ><i class="famfamfam-silk delete"></i>Deactivate</span
                >
            </b-button>
            <a href="#0" @click.prevent="activityHistory(activity)">
                <i class="famfamfam-silk book mx-1"></i>History
            </a>
        </b-popover>
        <!-- <i class="fa fa-chevron-right"></i> -->
    </div>
</template>
<style>
.popover.activity-popover {
    min-width: 25rem;
}

.alert-delete {
    background-color: #ff9955;
}
</style>
<script>
import { DateTime } from "luxon";
import { get, find, sortBy, filter, some, union } from "lodash";
import ScheduleHelper from "../helper";
import SummaryList from "./summary-list";
import Records from "@/services/records";
import InvoiceItemService from "@/services/invoice/invoice-item";

export default {
    name: "ActivitiesCards",
    components: {
        SummaryList
    },
    mixins: [ScheduleHelper],
    props: {
        data: {
            type: Object,
            default() {
                return {};
            }
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {};
    },
    computed: {
        status() {
            return find(this.data.treatment_status, { isactive: true });
        },
        activitiesFilter() {
            return this.status &&
                this.status.status_id > this.TREATMENT_STATUS.ADMITTED
                ? sortBy(
                      filter(
                          this.data.activities.map(activity => ({
                              ...activity,
                              dueCalculated: this._calculateDue(
                                  DateTime.fromSeconds(this.data.start),
                                  activity.due,
                                  activity.due_type,
                                  this._now()
                              )
                          })),
                          { isactive: true, status: 0 }
                      ),
                      activity => get(activity, "dueCalculated.time")
                  )
                : [];
        },
        activitiesCompletedFilter() {
            return sortBy(
                filter(
                    this.data.activities.map(activity => ({
                        ...activity,
                        dueCalculated: this._calculateDue(
                            this._fromDateCarbon(
                                this.data.treatment_status[0].create_at
                            ),
                            activity.due,
                            activity.due_type,
                            this._now()
                        )
                    })),
                    activity =>
                        activity.isactive &&
                        (this.status.status_id > this.TREATMENT_STATUS.ADMITTED
                            ? activity.status === 1 || activity.status === 3
                            : true)
                ),
                activity => activity.complete_at
            );
        },
        popOverFilter() {
            if (
                this.status &&
                this.status.status_id > this.TREATMENT_STATUS.ADMITTED &&
                (this._hasAccess("schedule.treatmentboard.delete") ||
                    this._hasAccess("schedule.treatmentboard.update")) &&
                !this._isTvView
            ) {
                return union(
                    this.activitiesFilter,
                    this.activitiesCompletedFilter
                );
            } else {
                return [];
            }
        }
    },
    watch: {},
    methods: {
        start() {
            if (
                this._hasAccess("schedule.treatmentboard.start") &&
                !this._isTvView
            ) {
                this.dialogs["startModal"].open({
                    patient: this.data.patient,
                    clientID: this.data.client_id,
                    treatmentID: this.data.id,
                    treatment: this.data,
                    weight: find(this.vitals, { type: 4 })
                });
            }
        },
        fill(activity) {
            if (this._isTvView) {
                return;
            }
            this.dialogShow(activity.id);
            switch (activity.treatment_activity_type_id) {
                case 1:
                    this.dialogs["createUpdateRecordModal"].open({
                        module: this.MODULE.TREATMENT_BOARD,
                        patient: this.data.patient,
                        staff: this.data.staffs,
                        clientID: this.data.client_id,
                        treatment: this.data,
                        activity: activity,
                        action: "new"
                    });
                    break;
                case 2:
                    this.dialogs["newVitalModal"].open({
                        patient: this.data.patient,
                        staff: this.data.staffs,
                        client: this.data.clients,
                        activity: activity,
                        treatment: this.data
                    });
                    break;
                case 3:
                    this.dialogs["startTaskModal"].open({
                        activity: activity,
                        patient: this.data.patient,
                        treatment: this.data
                    });
                    break;
                default:
                    console.error("Undefined activity type");
            }
            this.dialogHidden();
        },
        async edit(activity) {
            if (this._isTvView) {
                return;
            }
            this.dialogShow(activity.id);
            if (activity.status === 1) {
                switch (activity.treatment_activity_type_id) {
                    case 1:
                        this.openMedicalRecordEdit(activity);
                        break;
                    case 2:
                        this.dialogs["newVitalModal"].open({
                            patient: this.data.patient,
                            staff: this.data.staffs,
                            client: this.data.clients,
                            activity: activity,
                            action: "editRecord"
                        });
                        break;
                    case 3:
                        this.dialogs["startTaskModal"].open({
                            activity: activity,
                            patient: this.data.patient,
                            action: "editRecord"
                        });
                        break;
                }
            } else {
                this.dialogs["editModal"].open({
                    ...activity,
                    action: activity.status === 3 ? "reactivate" : "edit",
                    patient: this.data.patient
                });
            }
            this.dialogHidden();
        },
        duplicate(activity) {
            if (this._isTvView) {
                return;
            }
            this.dialogShow(activity.id);
            // Modify the properties in the activity object as default value
            activity.occurence = 1;
            activity.specific_interval = 15;
            activity.repeat_interval = "minutes";
            activity.due_type = 2;

            this.dialogs["editModal"].open({
                ...activity,
                action: "duplicate",
                patient: this.data.patient
            });
        },
        summary(activity) {
            if (this._isTvView) {
                return;
            }
            this.dialogs["summaryModal"].open({
                ...activity,
                patient: this.data.patient
            });
        },
        end() {
            if (
                this._hasAccess("schedule.treatmentboard.end") &&
                !this._isTvView
            ) {
                this.dialogs["dischargeModal"].open({
                    ...this.data,
                    status: some(
                        this.data.activities.filter(
                            activity => activity.status === 0
                        )
                    )
                        ? this.TREATMENT_STATUS.CANCELLED
                        : this.TREATMENT_STATUS.DISCHARGED
                });
            }
        },
        deleteModal(activity) {
            this.dialogShow(activity.id);
            this.dialogs["deleteActivityModal"].open(
                activity,
                this.data.patient
            );
            this.dialogHidden();
        },
        removeTreatment() {
            this.dialogs["removeTreatmentModal"].open({
                ...this.data
            });
        },
        dialogShow(id) {
            this.$root.$once("bv::modal::shown", modal => {
                modal.vueTarget.activity_id = id;
                modal.vueTarget.treatment_id = this.data.id;

                let params = {
                    id,
                    time: this._now(),
                    staff: this.$root.currentActor,
                    treatment_id: this.data.id
                };

                this.$socket.client.emit("v-broadcast", {
                    params,
                    action: "activity-lock",
                    businessId: this._setting.business_id
                });
                this.$store.commit("locked_activity", params);
                window.addEventListener("beforeunload", this.activityUnlock);
            });
        },
        dialogHidden() {
            this.$root.$once("bv::modal::hide", this.activityUnlock);
        },
        activityUnlock(modal) {
            this.$socket.client.emit("v-broadcast", {
                params: {
                    id: modal.vueTarget.activity_id,
                    time: this._now(),
                    staff: this.$root.currentActor,
                    treatment_id: this.data.id
                },
                action: "activity-unlock",
                businessId: this._setting.business_id
            });
            this.$store.commit("locked_activity", null);
            window.removeEventListener("beforeunload", this.activityUnlock);
        },
        activityHistory(activity) {
            this.dialogs["modalActivityHistory"].open({
                ...activity,
                activity: activity
            });
        },
        getActivity(id) {
            return this.$http
                .get(process.env.BARRAMUNDI + "schedule/activity/" + id)
                .then(response => {
                    if (
                        response &&
                        response.data &&
                        response.data.response &&
                        response.data.response.resources
                    ) {
                        return response.data.response.resources;
                    }
                });
        },
        /**
         * @method openMedicalRecordEdit
         * @description Open modal edit medical record on treatment board activity once completed
         * handle in a method to avoid lexical declaration in case block (no-case-declarations)
         */
        async openMedicalRecordEdit(activity) {
            let editRecordDetails = {
                module: this.MODULE.TREATMENT_BOARD,
                patient: this.data.patient,
                staff: this.data.staffs,
                clientID: this.data.client_id,
                treatment: this.data,
                activity: activity,
                action: "edit"
            };

            const act = await this.getActivity(activity.id);

            if (act && act.medications) {
                // response act.medications return array of 1 object
                for (const medicalRecord of act.medications) {
                    const response = await Records.editRecordDetails(
                        medicalRecord.record_id
                    );
                    if (response) {
                        editRecordDetails.invoiceId =
                            response.form_data?.invoice_item_resource?.ID;
                        editRecordDetails.record_resources = response;
                        editRecordDetails.item_id =
                            response.form_data?.item_resource?.ID;
                    }
                }

                if (editRecordDetails.invoiceId) {
                    const invoiceItem = await InvoiceItemService.getInvoiceItem(
                        editRecordDetails.invoiceId
                    );

                    editRecordDetails.invoice_item = invoiceItem;
                }
            }

            this.dialogs["createUpdateRecordModal"].open(editRecordDetails);
        }
    }
};
</script>
